'use client';

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import getCtaStyles from '../button/get-cta-styles';
import RichTextTitle from '../rich-text-title';
import BevSafeText from '../bev-safe-text';
import AsymmetricalCard from './asymmetrical-card';
import CarouselArrows from './carousel-arrows';
import CarouselSlider from '../carousel/carousel-slider';
import useDragToScroll from '../carousel/use-drag-to-scroll';
import OpenTableModal from './open-table-modal';
import type {
	AsymmetricalCardData,
	CardReserve,
	CardVariant,
	CarouselAlign,
	FeaturedPosition,
} from './types';

const MAX_CARDS = 11;
const IMAGE_HEIGHT_CLASS = 'h-[394px] md:h-[366px]';

const ORDER_CLASS = [
	'order-none',
	'order-1',
	'order-2',
	'order-3',
	'order-4',
	'order-5',
	'order-6',
	'order-7',
	'order-8',
	'order-9',
	'order-10',
	'order-11',
];

function cardVariant(index: number, count: number): CardVariant {
	if (count <= 2) return 'landscape';
	return index === 0 ? 'landscape' : 'portrait';
}

function featuredOrderClass(index: number, count: number, reposition: boolean): string {
	if (!reposition || count < 3) return '';
	if (index === 0) return 'order-none md:order-1';
	if (index === 1) return 'order-1 md:order-none';
	return ORDER_CLASS[index] ?? '';
}

function cardOffsets(el: HTMLElement): number[] {
	const children = Array.from(el.children) as HTMLElement[];
	if (children.length === 0) return [];
	const base = children[0].offsetLeft;
	return children.map((child, index) => (index === 0 ? 0 : child.offsetLeft - base));
}

function indexAt(offsets: number[], scrollLeft: number, maxScroll?: number): number {
	if (maxScroll !== undefined && maxScroll > 0 && scrollLeft >= maxScroll - 1) {
		return offsets.length - 1;
	}
	let index = 0;
	for (let i = 0; i < offsets.length; i++) {
		if (offsets[i] <= scrollLeft + 1) index = i;
	}
	return index;
}

export type Props = {
	title: string;
	cards: AsymmetricalCardData[];
	id?: string;
	description?: string;
	ctaLabel?: string;
	ctaHref?: string;
	ctaNewWindow?: boolean;
	align?: CarouselAlign;
	featuredPosition?: FeaturedPosition;
	minCards?: number;
	cardTitleClamp?: 2 | 3;
	previousLabel?: string;
	nextLabel?: string;
	reserveTranslations?: Record<string, string>;
	className?: string;
};

export default function AsymmetricalCardCarousel({
	title,
	cards,
	id,
	description,
	ctaLabel,
	ctaHref,
	ctaNewWindow,
	align = 'left',
	featuredPosition = 'first',
	minCards = 1,
	cardTitleClamp = 2,
	previousLabel,
	nextLabel,
	reserveTranslations,
	className,
}: Props): ReactElement | null {
	const t = useTranslations();
	const scrollRef = useRef<HTMLDivElement>(null);

	useDragToScroll(scrollRef);
	const [metrics, setMetrics] = useState({
		scrollLeft: 0,
		scrollWidth: 0,
		clientWidth: 0,
		mediaHeight: 0,
	});
	const [activeReserve, setActiveReserve] = useState<CardReserve | null>(null);

	const openReserve = useCallback((card: AsymmetricalCardData) => {
		if (card.reserve) {
			setActiveReserve(card.reserve);
		}
	}, []);

	const closeReserve = useCallback(() => setActiveReserve(null), []);

	const items = cards.slice(0, MAX_CARDS);

	const isSingle = items.length === 1;

	const measure = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		const firstCard = el.firstElementChild as HTMLElement | null;
		const media = firstCard?.firstElementChild as HTMLElement | null;
		setMetrics({
			scrollLeft: el.scrollLeft,
			scrollWidth: el.scrollWidth,
			clientWidth: el.clientWidth,
			mediaHeight: media?.getBoundingClientRect().height ?? 0,
		});
	}, []);

	useEffect(() => {
		measure();
		const el = scrollRef.current;
		if (!el) return;
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [measure, items.length]);

	const maxScroll = Math.max(0, metrics.scrollWidth - metrics.clientWidth);
	const canScrollPrev = metrics.scrollLeft > 1;
	const canScrollNext = metrics.scrollLeft < maxScroll - 1;
	const scrollable = maxScroll > 1;

	const widthPct = scrollable
		? Math.min(100, (metrics.clientWidth / metrics.scrollWidth) * 100)
		: 100;
	const offsetPct = maxScroll > 0 ? (metrics.scrollLeft / metrics.scrollWidth) * 100 : 0;

	const handleNext = () => {
		const el = scrollRef.current;
		if (!el) return;
		const offsets = cardOffsets(el);
		if (offsets.length < 2) return;

		const last = offsets.length - 1;
		const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
		const current = indexAt(offsets, el.scrollLeft, maxScroll);

		let nextIndex = current <= 0 ? 3 : current + 4;
		if (nextIndex > last) nextIndex = last;
		if (nextIndex <= current) return;

		el.scrollLeft = Math.min(offsets[nextIndex], maxScroll);
	};

	const handlePrev = () => {
		const el = scrollRef.current;
		if (!el) return;
		const offsets = cardOffsets(el);
		if (offsets.length < 2) return;

		const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
		const current = indexAt(offsets, el.scrollLeft, maxScroll);
		if (current <= 0) return;

		const prevIndex = current <= 3 ? 0 : current - 4;
		if (prevIndex >= current) return;

		el.scrollLeft = offsets[prevIndex];
	};

	if (items.length < Math.max(1, minCards)) return null;

	const showCta = Boolean(ctaLabel && ctaHref);
	const reposition = featuredPosition === 'second';
	const centerTwoCardTrack = align === 'center' && items.length === 2;
	// Whether the track overflows the content column, and so bleeds full-width and
	// shows the pagination bar. 4+ cards overflow at every tier; 2-3 only fit side by
	// side from lg up (IAI-345), so they bleed below lg only. Keyed to the breakpoint
	// rather than measured, so it's settled on first paint (SSR) and testable at a
	// fixed viewport.
	const bleed: 'always' | 'below-lg' | 'never' =
		items.length > 3 ? 'always' : isSingle ? 'never' : 'below-lg';
	const responsiveDesktopTrack = items.length >= 3;

	const cta = showCta && (
		<a
			href={ctaHref}
			target={ctaNewWindow ? '_blank' : undefined}
			rel={ctaNewWindow ? 'noopener' : undefined}
			className={getCtaStyles({
				intent: 'filled',
				size: 'small',
				className: 'md:whitespace-nowrap',
			})}
		>
			{ctaLabel}
		</a>
	);

	const header =
		align === 'center' ? (
			<div className="flex flex-col items-center gap-y-heading-s text-center">
				<RichTextTitle
					title={title}
					id={id}
					className="text-display3 line-clamp-2 text-text-heading"
				/>
				{(description || cta) && (
					<div className="flex flex-col items-center gap-y-stack-l">
						{description && (
							<p className="text-h3 text-text-primary max-w-[723px]">
								<BevSafeText>{description}</BevSafeText>
							</p>
						)}
						{cta}
					</div>
				)}
			</div>
		) : (
			<div className="flex flex-col items-start gap-y-stack-l gap-x-300 md:flex-row md:items-end md:justify-between">
				<div className="flex w-full flex-col gap-y-heading-s md:w-[723px] xl:w-[968px]">
					<RichTextTitle
						title={title}
						id={id}
						className="text-display3 line-clamp-2 pb-[0.15em] text-text-heading"
					/>
					{description && (
						<p className="text-h3 text-text-primary">
							<BevSafeText>{description}</BevSafeText>
						</p>
					)}
				</div>
				{cta}
			</div>
		);

	return (
		// The section spans its parent uncapped so the track can bleed to the parent's
		// edge at any width; everything else sits in the wide grid (decision b96ff0).
		<section className={cn('flex w-full flex-col spacing-stack-l', className)}>
			<div className="w-full spacing-wide-grid">{header}</div>

			<div
				className={cn(
					'group relative',
					bleed === 'never' && 'spacing-wide-grid-inset',
					bleed === 'below-lg' && 'lg:spacing-wide-grid-inset'
				)}
			>
				<div
					ref={scrollRef}
					onScroll={measure}
					className={cn(
						'no-scrollbar flex snap-x snap-mandatory gap-x-grid-gutter overflow-x-auto scroll-smooth',
						responsiveDesktopTrack &&
							'lg:[--card-media-height:calc((100%-60px)*366/1199)] [--portrait-card-width:calc(var(--card-media-height)*292/366)] [--landscape-card-width:calc(var(--card-media-height)*615/366)]',
						centerTwoCardTrack && 'lg:grid lg:grid-cols-2',
						bleed === 'always' && 'spacing-wide-grid-bleed',
						bleed === 'below-lg' && 'max-lg:spacing-wide-grid-bleed'
					)}
					style={{ scrollbarWidth: 'none' }}
				>
					{items.map((card, index) => (
						<AsymmetricalCard
							key={card.id}
							card={card}
							variant={cardVariant(index, items.length)}
							titleClamp={cardTitleClamp}
							// a single card spans the whole row; multi-card widths (landscape ≈ half,
							// portrait ≈ a quarter) are left to the card's own variant-based default
							roughSize={isSingle ? 'l' : undefined}
							widthClassName={
								isSingle
									? 'w-full'
									: responsiveDesktopTrack
										? index === 0
											? 'w-[315px] md:w-[615px] lg:w-[var(--landscape-card-width)]'
											: 'w-[315px] md:w-[292px] lg:w-[var(--portrait-card-width)]'
										: undefined
							}
							aspectClassName={isSingle ? 'aspect-[345/431] md:aspect-[16/9]' : undefined}
							className={cn(
								featuredOrderClass(index, items.length, reposition),
								centerTwoCardTrack && 'lg:w-full'
							)}
							onReserve={openReserve}
						/>
					))}
				</div>

				<CarouselArrows
					canScrollPrev={canScrollPrev}
					canScrollNext={canScrollNext}
					onPrev={handlePrev}
					onNext={handleNext}
					imageHeightClass={IMAGE_HEIGHT_CLASS}
					imageHeight={metrics.mediaHeight}
					labels={{
						previous: previousLabel ?? t('Previous cards'),
						next: nextLabel ?? t('Next cards'),
					}}
					bleed={bleed}
				/>
			</div>

			{bleed !== 'never' && (
				<div className={cn('w-full spacing-wide-grid', bleed === 'below-lg' && 'lg:hidden')}>
					<CarouselSlider widthPct={widthPct} offsetPct={offsetPct} />
				</div>
			)}

			{activeReserve && (
				<OpenTableModal
					reserve={activeReserve}
					close={closeReserve}
					translations={reserveTranslations}
				/>
			)}
		</section>
	);
}
